/* global window, document, fetch, FormData, sessionStorage */
(function () {
  "use strict";

  const STORAGE_KEY = "mfReturnsFlowState";
  // fee: change-of-mind reasons incur fees (AUD 30/unit + customs).
  // requiresComment: seller-fault reasons must include a description of the issue.
  // Starred (*) reasons require additional info in the comments box — the
  // asterisk is referenced next to that box. Fee details live in the Refund
  // Policy block below the item list, not in the option labels.
  const RETURN_REASONS = [
    { code: "changed_mind", label: "I changed my mind", fee: true },
    { code: "wrong_size", label: "I ordered the wrong size", fee: true },
    { code: "color_different", label: "The colour is different than I expected", fee: true },
    { code: "not_as_described", label: "Item is not as described*", fee: false, requiresComment: true },
    { code: "faulty", label: "Faulty, defective, or doesn't perform as described*", fee: false, requiresComment: true },
    { code: "wrong_item_received", label: "Delivery issues / Wrong item received*", fee: false, requiresComment: true },
  ];
  const CHANGE_OF_MIND_REASON_CODES = RETURN_REASONS.filter(function (r) {
    return r.fee;
  }).map(function (r) {
    return r.code;
  });
  const COMMENT_REQUIRED_REASON_CODES = RETURN_REASONS.filter(function (r) {
    return r.requiresComment;
  }).map(function (r) {
    return r.code;
  });

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    return "rf-" + Date.now() + "-" + Math.random().toString(36).slice(2, 10);
  }

  function ReturnsFlow(root) {
    this.root = root;
    this.uploadsInFlight = 0;
    this.proxyBase = root.getAttribute("data-proxy-base") || "/apps/returns";
    this.returnFeeAud = parseFloat(root.getAttribute("data-return-fee-aud") || "30");
    this.customsFeeAud = parseFloat(root.getAttribute("data-customs-fee-aud") || "95");
    this.customsFeeThresholdAud = parseFloat(
      root.getAttribute("data-customs-fee-threshold-aud") || "1000",
    );
    this.steps = Array.from(root.querySelectorAll(".returns-step"));
    this.progressItems = Array.from(root.querySelectorAll(".returns-flow__progress li"));
    this.errorBanner = root.querySelector("[data-error]");
    this.state = this.loadState() || {
      flowId: uuid(),
      currentStep: 1,
      eligibility: null,
      lookup: null,
      orderName: "",
      email: "",
      selectedItems: [],
      customerComments: "",
      photos: [],
      feeAcknowledged: false,
      referenceNumber: null,
    };
    // Migration for state saved before timeline navigation existed.
    if (!this.state.maxStep) this.state.maxStep = this.state.currentStep;

    this.bindGlobalEvents();
    this.bindStepEvents();
    // No heading focus on the initial render — it draws a focus ring on the
    // step title for every visitor. Focus only on step changes (a11y).
    this.renderStep({ skipFocus: true });
  }

  ReturnsFlow.prototype.loadState = function () {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_e) {
      return null;
    }
  };

  ReturnsFlow.prototype.saveState = function () {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch (_e) {
      /* ignore quota errors */
    }
  };

  ReturnsFlow.prototype.clearState = function () {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (_e) {
      /* ignore */
    }
  };

  ReturnsFlow.prototype.bindGlobalEvents = function () {
    const self = this;
    this.steps.forEach(function (step) {
      step.querySelectorAll('[data-action="back"]').forEach(function (btn) {
        btn.addEventListener("click", function () {
          self.goBack();
        });
      });
    });
    // Timeline navigation: completed steps are clickable to jump back.
    // Moving forward again goes through each step's Continue (re-validates).
    this.progressItems.forEach(function (li) {
      const n = parseInt(li.getAttribute("data-step"), 10);
      function activate() {
        if (self.state.currentStep >= 6) return; // submitted — no going back
        if (n < self.state.currentStep) self.goTo(n);
      }
      li.addEventListener("click", activate);
      li.addEventListener("keydown", function (e) {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          activate();
        }
      });
    });
  };

  ReturnsFlow.prototype.goTo = function (n) {
    this.state.currentStep = Math.min(Math.max(n, 1), 6);
    this.saveState();
    this.renderStep();
  };

  ReturnsFlow.prototype.bindStepEvents = function () {
    const self = this;
    const elig = this.root.querySelector('[data-form="eligibility"]');
    if (elig) {
      elig.addEventListener("submit", function (e) {
        e.preventDefault();
        self.handleEligibilitySubmit(elig);
      });
    }

    const lookup = this.root.querySelector('[data-form="lookup"]');
    if (lookup) {
      lookup.addEventListener("submit", function (e) {
        e.preventDefault();
        self.handleLookupSubmit(lookup);
      });
    }

    const items = this.root.querySelector('[data-form="items"]');
    if (items) {
      items.addEventListener("submit", function (e) {
        e.preventDefault();
        self.handleItemsSubmit(items);
      });
    }

    const photos = this.root.querySelector('[data-form="photos"]');
    if (photos) {
      photos.addEventListener("submit", function (e) {
        e.preventDefault();
        self.handlePhotosSubmit();
      });
    }
    this.bindPhotoDropzone();

    const fees = this.root.querySelector('[data-form="fees"]');
    if (fees) {
      fees.addEventListener("submit", function (e) {
        e.preventDefault();
        self.handleFinalSubmit(fees);
      });
    }
  };

  ReturnsFlow.prototype.renderStep = function (opts) {
    const target = this.state.currentStep;
    this.steps.forEach(function (step) {
      const n = parseInt(step.getAttribute("data-step"), 10);
      step.hidden = n !== target;
    });
    const submitted = target >= 6;
    this.progressItems.forEach(function (li) {
      const n = parseInt(li.getAttribute("data-step"), 10);
      li.classList.toggle("is-active", n === target);
      li.classList.toggle("is-done", n < target);
      const clickable = !submitted && n < target;
      li.classList.toggle("is-clickable", clickable);
      if (clickable) {
        li.setAttribute("tabindex", "0");
        li.setAttribute("role", "button");
        li.setAttribute("aria-label", "Go back to step " + n);
      } else {
        li.removeAttribute("tabindex");
        li.removeAttribute("role");
        li.removeAttribute("aria-label");
      }
    });
    this.hideError();
    if (target === 3) this.renderItems();
    if (target === 5) this.renderFees();
    if (target === 6) this.renderConfirmation();
    // Focus the first heading for screen readers (skipped on initial render).
    if (!(opts && opts.skipFocus)) {
      const heading = this.steps[target - 1] && this.steps[target - 1].querySelector(".returns-step__title");
      if (heading) heading.setAttribute("tabindex", "-1"), heading.focus();
      window.scrollTo({ top: this.root.offsetTop - 24, behavior: "smooth" });
    }
  };

  ReturnsFlow.prototype.goNext = function () {
    this.state.currentStep = Math.min(this.state.currentStep + 1, 6);
    this.state.maxStep = Math.max(this.state.maxStep || 1, this.state.currentStep);
    this.saveState();
    this.renderStep();
  };

  ReturnsFlow.prototype.goBack = function () {
    this.state.currentStep = Math.max(this.state.currentStep - 1, 1);
    this.saveState();
    this.renderStep();
  };

  ReturnsFlow.prototype.showError = function (msg) {
    if (!this.errorBanner) return;
    this.errorBanner.textContent = msg;
    this.errorBanner.hidden = false;
  };

  ReturnsFlow.prototype.hideError = function () {
    if (!this.errorBanner) return;
    this.errorBanner.textContent = "";
    this.errorBanner.hidden = true;
  };

  ReturnsFlow.prototype.setLoading = function (btn, loading) {
    if (!btn) return;
    btn.disabled = !!loading;
    const idle = btn.querySelector("[data-label-idle]");
    const load = btn.querySelector("[data-label-loading]");
    if (idle && load) {
      idle.hidden = !!loading;
      load.hidden = !loading;
    }
  };

  ReturnsFlow.prototype.postJson = function (body) {
    return fetch(this.proxyBase, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
      credentials: "same-origin",
    }).then(function (res) {
      return res
        .json()
        .catch(function () {
          return { error: "Server returned an invalid response (status " + res.status + ")." };
        })
        .then(function (json) {
          if (!res.ok && !json.error) {
            json.error = "Request failed with status " + res.status;
          }
          return json;
        });
    });
  };

  // ── Step 1: eligibility ───────────────────────────────────────────────────
  ReturnsFlow.prototype.handleEligibilitySubmit = function (form) {
    const data = new FormData(form);
    const flags = {
      within14Days: data.has("within14Days"),
      unworn: data.has("unworn"),
      tagsIntact: data.has("tagsIntact"),
      packagingIntact: data.has("packagingIntact"),
      // The packaging checkbox covers boxes/outer packaging too — the backend
      // still expects the footwearBoxOk flag, so it mirrors packagingIntact.
      footwearBoxOk: data.has("packagingIntact"),
      firstRequest: data.has("firstRequest"),
    };
    const allTrue = Object.keys(flags).every(function (k) { return flags[k]; });
    const warning = form.querySelector("[data-eligibility-warning]");
    if (!allTrue) {
      if (warning) warning.hidden = false;
      return;
    }
    if (warning) warning.hidden = true;
    this.state.eligibility = flags;
    this.saveState();
    this.goNext();
  };

  // ── Step 2: order lookup ──────────────────────────────────────────────────
  ReturnsFlow.prototype.handleLookupSubmit = function (form) {
    const self = this;
    const data = new FormData(form);
    const orderName = String(data.get("orderName") || "").trim();
    const email = String(data.get("email") || "").trim();
    const errorEl = form.querySelector("[data-lookup-error]");
    const btn = form.querySelector('[data-action="lookup-submit"]');
    if (errorEl) errorEl.hidden = true;

    if (!orderName || !email) {
      if (errorEl) {
        errorEl.textContent = "Please enter your order number and the email used to place the order.";
        errorEl.hidden = false;
      }
      return;
    }

    this.setLoading(btn, true);
    this.postJson({ intent: "lookup", orderName: orderName, email: email })
      .then(function (resp) {
        if (resp.eligible === false) {
          if (errorEl) {
            errorEl.textContent = resp.message || "We couldn't validate this order.";
            errorEl.hidden = false;
          }
          return;
        }
        if (!resp.items || resp.items.length === 0) {
          if (errorEl) {
            errorEl.textContent = "No returnable items found on this order.";
            errorEl.hidden = false;
          }
          return;
        }
        self.state.orderName = resp.orderName;
        self.state.email = email;
        self.state.lookup = resp;
        self.state.selectedItems = [];
        self.saveState();
        self.goNext();
      })
      .catch(function (err) {
        if (errorEl) {
          errorEl.textContent = (err && err.message) || "We couldn't reach the returns service. Please try again.";
          errorEl.hidden = false;
        }
      })
      .finally(function () {
        self.setLoading(btn, false);
      });
  };

  // ── Step 3: items ─────────────────────────────────────────────────────────
  ReturnsFlow.prototype.renderItems = function () {
    const summary = this.root.querySelector("[data-order-summary]");
    const list = this.root.querySelector("[data-items-list]");
    if (!summary || !list || !this.state.lookup) return;

    summary.innerHTML =
      '<p><strong>Order:</strong> ' +
      this.escapeHtml(this.state.lookup.orderName) +
      "</p>" +
      "<p><strong>Delivered:</strong> " +
      this.escapeHtml(new Date(this.state.lookup.deliveredAt).toLocaleDateString()) +
      " (" +
      this.state.lookup.daysSinceDelivery +
      " day(s) ago)</p>";

    list.innerHTML = "";
    const self = this;
    this.state.lookup.items.forEach(function (item, idx) {
      const li = document.createElement("div");
      li.className = "returns-item-row";
      const checkbox =
        '<label class="returns-item-row__select"><input type="checkbox" data-item-checkbox data-line-id="' +
        self.escapeHtml(item.shopifyLineItemId) +
        '"><span>' +
        self.escapeHtml(item.title) +
        "</span></label>";
      const meta =
        '<div class="returns-item-row__meta">' +
        (item.sku ? "<small>SKU " + self.escapeHtml(item.sku) + "</small>" : "") +
        '<span class="returns-item-row__price">AUD ' +
        item.unitPriceAud.toFixed(2) +
        "</span></div>";
      const qty =
        '<div class="returns-item-row__qty"><label>Qty' +
        '<input type="number" min="1" max="' +
        item.remainingQuantity +
        '" value="1" data-item-qty data-line-id="' +
        self.escapeHtml(item.shopifyLineItemId) +
        '"></label></div>';
      const reasonOptions = RETURN_REASONS.map(function (r) {
        return (
          '<option value="' + self.escapeHtml(r.code) + '">' +
          self.escapeHtml(r.label) +
          "</option>"
        );
      }).join("");
      const reason =
        '<div class="returns-item-row__reason"><label>Reason' +
        '<select data-item-reason data-line-id="' +
        self.escapeHtml(item.shopifyLineItemId) +
        '"><option value="">Select a reason</option>' +
        reasonOptions +
        "</select></label></div>";
      li.innerHTML = checkbox + meta + qty + reason;
      list.appendChild(li);
    });

    // Comments become required when any selected reason is a seller-fault one.
    if (!list.dataset.commentsBound) {
      list.dataset.commentsBound = "1";
      list.addEventListener("change", function () {
        self.updateCommentsRequirement();
      });
    }
    this.updateCommentsRequirement();
  };

  ReturnsFlow.prototype.commentsRequired = function () {
    const selected = this.collectSelectedItems();
    for (let i = 0; i < selected.length; i++) {
      if (COMMENT_REQUIRED_REASON_CODES.indexOf(selected[i].reasonCode) !== -1) return true;
    }
    return false;
  };

  ReturnsFlow.prototype.updateCommentsRequirement = function () {
    const label = this.root.querySelector("[data-comments-label]");
    const textarea = this.root.querySelector("#returns-comments");
    const required = this.commentsRequired();
    if (label) {
      // The leading * only appears while a starred (comment-required) reason
      // is selected — it mirrors the asterisk on those dropdown options.
      label.textContent = required
        ? "* Additional comments (required — please describe the issue)"
        : "Additional comments (optional)";
    }
    if (textarea) {
      if (required) textarea.setAttribute("required", "required");
      else textarea.removeAttribute("required");
    }
  };

  ReturnsFlow.prototype.collectSelectedItems = function () {
    const checkboxes = this.root.querySelectorAll("[data-item-checkbox]:checked");
    const result = [];
    for (let i = 0; i < checkboxes.length; i++) {
      const id = checkboxes[i].getAttribute("data-line-id");
      const qtyInput = this.root.querySelector('[data-item-qty][data-line-id="' + cssEscape(id) + '"]');
      const reasonSelect = this.root.querySelector('[data-item-reason][data-line-id="' + cssEscape(id) + '"]');
      const qty = parseInt((qtyInput && qtyInput.value) || "1", 10);
      const reasonCode = (reasonSelect && reasonSelect.value) || "";
      result.push({ shopifyLineItemId: id, quantity: qty, reasonCode: reasonCode });
    }
    return result;
  };

  function cssEscape(s) {
    return String(s).replace(/(["\\\]])/g, "\\$1");
  }

  ReturnsFlow.prototype.handleItemsSubmit = function (form) {
    const errorEl = form.querySelector("[data-items-error]");
    const selected = this.collectSelectedItems();
    if (selected.length === 0) {
      if (errorEl) {
        errorEl.textContent = "Please select at least one item to return.";
        errorEl.hidden = false;
      }
      return;
    }
    for (let i = 0; i < selected.length; i++) {
      if (!selected[i].reasonCode) {
        if (errorEl) {
          errorEl.textContent = "Please choose a reason for every selected item.";
          errorEl.hidden = false;
        }
        return;
      }
    }
    const comments = String((new FormData(form).get("customerComments")) || "").trim();
    if (this.commentsRequired() && !comments) {
      if (errorEl) {
        errorEl.textContent =
          "Please provide additional information in the comments — it's required for the reason you selected.";
        errorEl.hidden = false;
      }
      return;
    }
    this.state.selectedItems = selected;
    this.state.customerComments = comments;
    if (errorEl) errorEl.hidden = true;
    this.saveState();
    this.goNext();
  };

  // ── Step 4: photos ────────────────────────────────────────────────────────
  ReturnsFlow.prototype.bindPhotoDropzone = function () {
    const self = this;
    const dz = this.root.querySelector("[data-dropzone]");
    const input = this.root.querySelector("[data-file-input]");
    if (!dz || !input) return;
    dz.addEventListener("click", function (e) {
      if (e.target === dz || e.target.closest(".returns-dropzone__label") === null) {
        input.click();
      }
    });
    dz.addEventListener("keydown", function (e) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        input.click();
      }
    });
    dz.addEventListener("dragover", function (e) {
      e.preventDefault();
      dz.classList.add("is-dragging");
    });
    dz.addEventListener("dragleave", function () {
      dz.classList.remove("is-dragging");
    });
    dz.addEventListener("drop", function (e) {
      e.preventDefault();
      dz.classList.remove("is-dragging");
      if (e.dataTransfer && e.dataTransfer.files) {
        self.queuePhotoUploads(e.dataTransfer.files);
      }
    });
    input.addEventListener("change", function () {
      if (input.files) self.queuePhotoUploads(input.files);
      input.value = "";
    });
  };

  ReturnsFlow.prototype.queuePhotoUploads = function (fileList) {
    const errorEl = this.root.querySelector("[data-photos-error]");
    if (errorEl) errorEl.hidden = true;
    const self = this;
    Array.prototype.forEach.call(fileList, function (file) {
      // Browsers often report an empty type for HEIC — fall back to extension.
      const typeOk =
        /^image\/(jpeg|png|webp|heic|heif)$/.test(file.type) ||
        (!file.type && /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name));
      if (!typeOk) {
        if (errorEl) {
          errorEl.textContent = "Only JPEG, PNG, WebP, and HEIC images are accepted.";
          errorEl.hidden = false;
        }
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        if (errorEl) {
          errorEl.textContent = "Each photo must be under 10 MB.";
          errorEl.hidden = false;
        }
        return;
      }
      self.uploadOnePhoto(file);
    });
  };

  function mimeForFile(file) {
    if (file.type) return file.type;
    const m = /\.([a-z0-9]+)$/i.exec(file.name || "");
    const ext = m ? m[1].toLowerCase() : "";
    if (ext === "heic") return "image/heic";
    if (ext === "heif") return "image/heif";
    if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
    if (ext === "png") return "image/png";
    if (ext === "webp") return "image/webp";
    return "application/octet-stream";
  }

  ReturnsFlow.prototype.uploadOnePhoto = function (file) {
    const self = this;
    const mimeType = mimeForFile(file);
    const grid = this.root.querySelector("[data-photo-grid]");
    if (!grid) return;
    this.uploadsInFlight++;
    const li = document.createElement("li");
    li.className = "returns-photo-grid__item is-uploading";
    li.innerHTML =
      '<div class="returns-photo-grid__preview"></div>' +
      '<div class="returns-photo-grid__status">Uploading…</div>' +
      '<button type="button" class="returns-photo-grid__remove" aria-label="Remove" hidden>×</button>';
    grid.appendChild(li);

    const previewEl = li.querySelector(".returns-photo-grid__preview");
    const reader = new FileReader();
    reader.onload = function (ev) {
      if (previewEl) {
        previewEl.style.backgroundImage = "url(" + ev.target.result + ")";
      }
    };
    reader.readAsDataURL(file);

    // 1. Stage upload via proxy
    this.postJson({
      intent: "photo-stage",
      filename: file.name,
      mimeType: mimeType,
      fileSize: file.size,
    })
      .then(function (stageResp) {
        if (!stageResp.ok) throw new Error(stageResp.error || "Failed to prepare upload.");
        // 2. POST file + parameters to staged URL
        const fd = new FormData();
        if (Array.isArray(stageResp.parameters)) {
          stageResp.parameters.forEach(function (p) {
            fd.append(p.name, p.value);
          });
        }
        fd.append("file", file);
        return fetch(stageResp.uploadUrl, { method: "POST", body: fd }).then(function (r) {
          if (!r.ok) throw new Error("Upload to Shopify storage failed (status " + r.status + ").");
          return stageResp;
        });
      })
      .then(function (stageResp) {
        // 3. Finalize as MediaImage via proxy
        return self.postJson({
          intent: "photo-finalize",
          resourceUrl: stageResp.resourceUrl,
          filename: file.name,
          mimeType: mimeType,
        });
      })
      .then(function (finalResp) {
        if (!finalResp.ok) throw new Error(finalResp.error || "Failed to finalize upload.");
        const photo = {
          shopifyFileId: finalResp.shopifyFileId,
          url: finalResp.url || "",
          width: finalResp.width,
          height: finalResp.height,
          sizeBytes: file.size,
        };
        self.state.photos.push(photo);
        self.saveState();
        li.classList.remove("is-uploading");
        const status = li.querySelector(".returns-photo-grid__status");
        if (status) status.textContent = "Uploaded";
        // Clear a stale "at least one photo is required" error — the user may
        // have pressed Continue while this upload was still in flight.
        const photosError = self.root.querySelector("[data-photos-error]");
        if (photosError) photosError.hidden = true;
        const remove = li.querySelector(".returns-photo-grid__remove");
        if (remove) {
          remove.hidden = false;
          remove.addEventListener("click", function () {
            const idx = self.state.photos.indexOf(photo);
            if (idx >= 0) self.state.photos.splice(idx, 1);
            self.saveState();
            li.remove();
          });
        }
      })
      .catch(function (err) {
        li.classList.remove("is-uploading");
        li.classList.add("is-failed");
        const status = li.querySelector(".returns-photo-grid__status");
        if (status) status.textContent = (err && err.message) || "Upload failed";
      })
      .finally(function () {
        self.uploadsInFlight = Math.max(0, self.uploadsInFlight - 1);
      });
  };

  ReturnsFlow.prototype.handlePhotosSubmit = function () {
    const errorEl = this.root.querySelector("[data-photos-error]");
    if (this.state.photos.length === 0) {
      if (errorEl) {
        // Distinguish "nothing chosen" from "still uploading" — on slow mobile
        // connections users press Continue before the upload settles.
        errorEl.textContent =
          this.uploadsInFlight > 0
            ? "Your photo is still uploading — wait for the “Uploaded” label, then press Continue again."
            : "At least one photo is required.";
        errorEl.hidden = false;
      }
      return;
    }
    if (errorEl) errorEl.hidden = true;
    this.goNext();
  };

  // ── Step 5: fees ──────────────────────────────────────────────────────────
  ReturnsFlow.prototype.computeFees = function () {
    let units = 0;
    let itemsSubtotal = 0;
    let changeOfMindUnits = 0;
    const lookupItemsById = {};
    (this.state.lookup ? this.state.lookup.items : []).forEach(function (it) {
      lookupItemsById[it.shopifyLineItemId] = it;
    });
    this.state.selectedItems.forEach(function (sel) {
      const lu = lookupItemsById[sel.shopifyLineItemId];
      if (lu) {
        units += sel.quantity;
        itemsSubtotal += lu.unitPriceAud * sel.quantity;
        // Fees apply only to change-of-mind items (faulty returns are free).
        if (CHANGE_OF_MIND_REASON_CODES.indexOf(sel.reasonCode) !== -1) {
          changeOfMindUnits += sel.quantity;
        }
      }
    });
    const returnFee = changeOfMindUnits * this.returnFeeAud;
    const orderValueAud = (this.state.lookup && this.state.lookup.orderValueAud) || 0;
    const customsFeeApplies =
      changeOfMindUnits > 0 && orderValueAud > this.customsFeeThresholdAud;
    const customsFee = customsFeeApplies ? this.customsFeeAud : 0;
    return {
      units: units,
      itemsSubtotal: itemsSubtotal,
      returnFee: returnFee,
      changeOfMindUnits: changeOfMindUnits,
      customsFeeApplies: customsFeeApplies,
      customsFee: customsFee,
    };
  };

  ReturnsFlow.prototype.renderFees = function () {
    const tbody = this.root.querySelector("[data-fees-table]");
    if (!tbody) return;
    const fees = this.computeFees();
    let html = "";
    html +=
      "<tr><th>Items subtotal (" +
      fees.units +
      " unit(s))</th><td>AUD " +
      fees.itemsSubtotal.toFixed(2) +
      "</td></tr>";
    if (fees.returnFee > 0) {
      html +=
        "<tr><th>Return fee (change-of-mind items)</th><td>− AUD " +
        fees.returnFee.toFixed(2) +
        "</td></tr>";
    }
    if (fees.customsFeeApplies) {
      html +=
        "<tr><th>Customs processing fee (order over AUD " +
        this.customsFeeThresholdAud +
        ")</th><td>− AUD " +
        fees.customsFee.toFixed(2) +
        "</td></tr>";
    }
    const finalRefund = Math.max(0, fees.itemsSubtotal - fees.returnFee - fees.customsFee);
    html +=
      '<tr class="returns-fees-table__total"><th>Final refund total</th><td>AUD ' +
      finalRefund.toFixed(2) +
      "</td></tr>";
    tbody.innerHTML = html;
  };

  ReturnsFlow.prototype.handleFinalSubmit = function (form) {
    const self = this;
    const ack = form.querySelector('input[name="feeAcknowledged"]');
    const errorEl = form.querySelector("[data-fees-error]");
    const btn = form.querySelector('[data-action="submit-final"]');
    if (!ack || !ack.checked) {
      if (errorEl) {
        errorEl.textContent = "Please acknowledge the fees before submitting.";
        errorEl.hidden = false;
      }
      return;
    }
    if (errorEl) errorEl.hidden = true;
    this.state.feeAcknowledged = true;
    this.saveState();

    this.setLoading(btn, true);
    this.postJson({
      intent: "submit",
      orderName: this.state.orderName,
      email: this.state.email,
      eligibilityFlags: this.state.eligibility,
      customerComments: this.state.customerComments,
      feeAcknowledged: true,
      items: this.state.selectedItems,
      photos: this.state.photos,
      idempotencyKey: this.state.flowId,
    })
      .then(function (resp) {
        if (resp.ok && resp.referenceNumber) {
          self.state.referenceNumber = resp.referenceNumber;
          self.saveState();
          self.goNext();
        } else {
          if (errorEl) {
            errorEl.textContent = (resp && resp.error) || "We couldn't submit your return. Please try again.";
            errorEl.hidden = false;
          }
        }
      })
      .catch(function (err) {
        if (errorEl) {
          errorEl.textContent = (err && err.message) || "We couldn't reach the returns service. Please try again.";
          errorEl.hidden = false;
        }
      })
      .finally(function () {
        self.setLoading(btn, false);
      });
  };

  // ── Step 6: confirmation ──────────────────────────────────────────────────
  ReturnsFlow.prototype.renderConfirmation = function () {
    const ref = this.root.querySelector("[data-confirmation-ref]");
    if (ref) {
      ref.textContent = this.state.referenceNumber || "";
    }
    // Clear stored state after a successful submission so a refresh on the
    // confirmation page starts a fresh flow.
    this.clearState();
  };

  ReturnsFlow.prototype.escapeHtml = function (s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  };

  function init() {
    const root = document.getElementById("returns-flow");
    if (!root) return;
    new ReturnsFlow(root);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
